/* eslint-disable react/no-unescaped-entities */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { PopupRow } from '@Pages/RetroSynthesis/ProjectParametersDialog/ProjectParametersUtils';
import { TextInput } from '@els/els-react--text-input';
import { Checkbox } from '@els/els-react--checkbox';
import { RadioButtonGroup } from '@els/els-react--radio-button';
import { SlideSwitch } from '@els/els-react--slide-switch';
import classnames from 'classnames';
import find from 'lodash/find';
import RPSSwitchIcon from '@Pages/RetroSynthesis/ProjectParametersDialog/RPS/RPSSwitchIcon';
import { isRCSChecked as isRCSCheckedFn } from '@Pages/RetroSynthesis/ProjectParametersDialog/RPS/mapRCSToRPS';
import {
  IK_PARAMS_MAX_NUMBER_OF_ROUTES_INFO_LABEL,
  IK_PARAMS_SELECTIVITY_INFO_LABEL,
  IK_PARAMS_MAX_SHIPPING_TIME,
  IK_PARAMS_SMS_INFO_LABEL,
  RCSANY,
  RCS10D,
  maxShippingTimeDisplayHelperObj,
  maxShippingTimeToggleHelperObj,
  PREDICTED_PARAMS_PROCESSING_TIME_LABEL,
  PREDICTED_PARAMS_MAX_PRICE_LABEL,
} from './constants';
import { PREDICTED_IKTOS_PROCESSING_TIME_INTERVALS } from '../constants';
import HeaderInfoIcon from '../ProjectRoutes/components/HeaderInfoIcon';
import HeaderInfoTooltip from '../ProjectRoutes/components/HeaderInfoTooltip';

// eslint-disable-next-line react/forbid-foreign-prop-types
RadioButtonGroup.Option.propTypes.label = PropTypes.oneOfType([PropTypes.string, PropTypes.node]);

function PredictedParametersIktosGeneral({
  params,
  onParamsChanged,
  buildingBlockElements,
  handleValidationError,
  defaultMaxNoOfSteps,
  isRCSAvailable,
  isRPSAvailable,
}) {
  const checkValidMaxNoSteps = (value) => {
    const n = Number(value);
    return !Number.isNaN(value) && n % 1 === 0 && n >= 2 && n <= 15;
  };

  const checkValidMaxPriceOfGrams = (value = '') => {
    if (!value) return true;
    if (`${value}`.replace(/\s/g, '') === '') return true;
    return !Number.isNaN(value) && Number(value) > 0 && Number(value) % 1 === 0;
  };

  const showSMSCheckbox = () => {
    if (buildingBlockElements.length <= 1) {
      return false;
    }
    return !(
      buildingBlockElements.length === 2 &&
      find(buildingBlockElements, (item) => item.name === RCS10D) &&
      find(buildingBlockElements, (item) => item.name === RCSANY)
    );
  };

  const isRCSChecked = () => {
    if (!params.usedBuildingBlocks) return false;
    return isRCSCheckedFn(params.usedBuildingBlocks) && !params.isRCSUnchecked;
  };

  const isAnyLibCheckBoxChecked = () => {
    if (!params.usedBuildingBlocks) return false;
    const isRCSCheckboxChecked = isRCSChecked();
    const noOfUserBBChecked = params.usedBuildingBlocks.filter((item) => [RCS10D, RCSANY].indexOf(item) < 0).length;
    return !!(isRCSCheckboxChecked || noOfUserBBChecked > 0);
  };

  useEffect(() => {
    const isMaxNoOfStepsValid = checkValidMaxNoSteps(params.maxNoOfSteps);
    const isMaxPricePerGramValid = checkValidMaxPriceOfGrams(params.maxPricePerGram);
    if (
      (!params.firstDisconnect && !isMaxNoOfStepsValid) ||
      !isMaxPricePerGramValid ||
      params.usedBuildingBlocks.length === 0 ||
      (params.isRCSUnchecked && params.usedBuildingBlocks.length === 1)
    ) {
      handleValidationError(true);
    } else {
      handleValidationError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.maxNoOfSteps, params.maxPricePerGram, params.firstDisconnect, params.usedBuildingBlocks, params.isRCSUnchecked]);

  useEffect(() => {
    if (params.firstDisconnect) {
      if (!checkValidMaxNoSteps(params.maxNoOfSteps)) {
        onParamsChanged({ maxNoOfSteps: defaultMaxNoOfSteps });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.firstDisconnect]);

  const onRPSSwitchChange = (event) => {
    const checked = event?.currentTarget?.checked;
    onParamsChanged({ isRPSChecked: checked });
  };

  return (
    <>
      <PopupRow className="rs-column-subheader">
        Length of routes
        <HeaderInfoIcon
          tooltipId="info-tooltip-predicted-length"
          e2eName="info-tooltip-predicted-length"
          ariaLabel={IK_PARAMS_MAX_NUMBER_OF_ROUTES_INFO_LABEL}
        />
        <HeaderInfoTooltip id="info-tooltip-predicted-length">
          <div className="rs-tooltip-big__content">
            <ul className="info-tooltip-content-list">
              <li className="info-tooltip-content-list-item bullet-dot">
                The algorithm aims to prepare synthesis routes for a target molecule within a 10-minute time limit. Only routes beginning
                with commercially or custom available starting materials are shown. A shorter maximum route length prompts the algorithm to
                halt route candidates sooner and explore alternative options faster. Consequently, shorter routes are typically more diverse
                but not lengthy. However, if the maximum route length is too short for the algorithm to find routes back to the chosen
                starting materials, no results may be produced. Please note: it is possible to enter between 2 to 15 No of steps (the
                default is 15).
              </li>
              <li className="info-tooltip-content-list-item bullet-dot">
                The option "First Disconnect only" will create synthesis suggestions for a given target for idea generation, but only show
                the first disconnection reaction step of a full route, without taking building block availability into account. The list of
                first disconnection step reaction can become very high (several hundred suggestions). When scanning through the suggestions
                in the routes overview or TreeView page you may select the ones you want to investigate further by clicking the
                corresponding starting material and select "Create retrosynthesis plans" from the 3-dot context menu of the given structure.
              </li>
            </ul>
          </div>
        </HeaderInfoTooltip>
      </PopupRow>

      <PopupRow className="rs-subrow mgt first-disconnect">
        <RadioButtonGroup.Option
          className="checkbox-firstDisconnect-no"
          checked={!params.firstDisconnect}
          label={<span>Max. Number of steps: </span>}
          onChange={(e) => {
            const { checked } = e.target;
            if (checked) {
              onParamsChanged({ firstDisconnect: false });
            }
          }}
        />
        <TextInput
          data-e2e="iktos-length-of-routes-input"
          value={params.maxNoOfSteps}
          label="max number of steps ranging from 2 to 15"
          shouldHideLabel
          placeholder=""
          disabled={params.firstDisconnect}
          size="small"
          min={2}
          max={15}
          className="iktos-length-of-routes-input"
          onChange={(e) => onParamsChanged({ maxNoOfSteps: e.target.value })}
        />
        (2-15)
      </PopupRow>
      <div
        aria-live="assertive"
        aria-atomic="true"
        className={classnames('length-of-routes-validation-messege', {
          'not-visible': params.firstDisconnect || checkValidMaxNoSteps(params.maxNoOfSteps),
        })}>
        {!params.firstDisconnect && !checkValidMaxNoSteps(params.maxNoOfSteps) ? 'Please enter a value between 2-15' : ''}
      </div>

      <PopupRow className="rs-subrow mgt">
        <RadioButtonGroup.Option
          className="checkbox-firstDisconnect-yes"
          checked={!!params.firstDisconnect}
          label={<span>First disconnect only</span>}
          onChange={(e) => {
            const { checked } = e.target;
            if (checked) {
              onParamsChanged({ firstDisconnect: true });
            }
          }}
        />
      </PopupRow>

      <hr className="grey-line" />
      <PopupRow className="rs-column-subheader mgt-sub-header">
        Stereo and Regioselectivity
        <HeaderInfoIcon
          tooltipId="info-tooltip-predicted-selectivity"
          e2eName="info-tooltip-predicted-selectivity"
          ariaLabel={IK_PARAMS_SELECTIVITY_INFO_LABEL}
        />
        <HeaderInfoTooltip id="info-tooltip-predicted-selectivity">
          <div className="rs-tooltip-big__content">
            <ul className="info-tooltip-content-list">
              <li className="info-tooltip-content-list-item bullet-dot">
                Stereochemistry is always considered if drawn explicitly. In case you get no results with steric substances please check
                “Ignore stereochemistry” and run the project again, to increase the probability to find routes.
              </li>
              <li className="info-tooltip-content-list-item bullet-dot">
                If the option “Only regioselective reactions” is selected, then the retrosynthesis algorithm will avoid routes which contain
                reaction steps with uncertain regioselectivity. Typically, this will result in a lower number of predicted routes.
              </li>
            </ul>
          </div>
        </HeaderInfoTooltip>
      </PopupRow>
      <PopupRow className="rs-subrow mgt">
        <Checkbox
          className="checkbox_no-padding iktos-checkbox-sm e2e-checkbox-stereo"
          checked={!params.poolChiral || false}
          data-e2e="iktos-ignore-stereochemistry-checkbox"
          onChange={() => onParamsChanged({ poolChiral: !params.poolChiral })}
          label="Ignore stereochemistry"
        />
      </PopupRow>
      <PopupRow className="rs-subrow mgt">
        <Checkbox
          className="checkbox_no-padding iktos-checkbox-sm e2e-checkbox-selectivity"
          checked={params.selectivity || false}
          data-e2e="iktos-regioselective-reactions-checkbox"
          onChange={() => onParamsChanged({ selectivity: !params.selectivity })}
          label="Only regioselective reactions"
        />
      </PopupRow>
      <hr className="grey-line" />
      <PopupRow className="rs-column-subheader mgt-sub-header">
        Starting materials settings
        <HeaderInfoIcon tooltipId="info-tooltip-predicted-sms" e2eName="info-tooltip-predicted-sms" ariaLabel={IK_PARAMS_SMS_INFO_LABEL} />
        <HeaderInfoTooltip id="info-tooltip-predicted-sms">
          <div className="rs-tooltip-big__content">
            Starting Materials are key parameters that guide the creation of synthetic routes. The route search halts when all starting
            materials are available in chosen commercial libraries. Only routes with such starting materials are provided to the user.
            Starting materials are sourced from Reaxys Commercial Substances (RCS), with the option to include custom libraries as well.
          </div>
        </HeaderInfoTooltip>
      </PopupRow>
      <div
        className={classnames('bblibs-validation-message', {
          'not-visible': isAnyLibCheckBoxChecked(),
        })}>
        Please select at least one Starting material library
      </div>
      {isRCSAvailable && (
        <>
          <PopupRow className="rs-subrow mgt">
            {showSMSCheckbox() ? (
              <Checkbox
                className="iktos-checkbox-sm e2e-checkbox-rcs"
                checked={isRCSChecked() || false}
                data-e2e="iktos-rcs-checkbox"
                onChange={() => {
                  if (params.isRCSUnchecked) {
                    const rcsBBChecked = params.usedBuildingBlocks?.indexOf(RCS10D) >= 0 || params.usedBuildingBlocks?.indexOf(RCSANY) >= 0;
                    onParamsChanged({
                      usedBuildingBlocks: rcsBBChecked ? params.usedBuildingBlocks : [...params.usedBuildingBlocks, RCS10D],
                      isRCSUnchecked: !params.isRCSUnchecked,
                    });
                  } else {
                    onParamsChanged({
                      isRCSUnchecked: !params.isRCSUnchecked,
                    });
                  }
                }}
                label="Reaxys Commercial Substances (RCS)"
              />
            ) : (
              <span>Reaxys Commercial Substances (RCS)</span>
            )}
          </PopupRow>
          {!params.isRCSUnchecked && (
            <>
              <PopupRow className="rs-subrow mgt rcs-subrow">
                Max. Shipping time
                <HeaderInfoIcon
                  tooltipId="info-tooltip-predicted-st"
                  e2eName="info-tooltip-predicted-st"
                  ariaLabel={IK_PARAMS_MAX_SHIPPING_TIME}
                />
                <HeaderInfoTooltip id="info-tooltip-predicted-st">
                  <div className="rs-tooltip-big__content">
                    With the default settings the algorithm uses all starting materials from Reaxys Commercial Substances (RCS), that can be
                    shipped within up to 10 days. Switch to &quot;any&quot;, if you want to use all RCS compounds. You may also enter a
                    pricing limit (Max. price per gram) that filters the list of available starting materials to the given limit.
                  </div>
                </HeaderInfoTooltip>
              </PopupRow>
              <PopupRow className="rs-subrow mgt rcs-subrow">
                {buildingBlockElements
                  ?.filter((block) => [RCS10D, RCSANY].indexOf(block.name) >= 0)
                  .map((element) => (
                    <RadioButtonGroup.Option
                      key={element.name}
                      className="checkbox_inline_right e2e-checkbox-max-shipping-time"
                      checked={params.usedBuildingBlocks && params.usedBuildingBlocks?.indexOf(element.name) >= 0}
                      label={<span>{maxShippingTimeDisplayHelperObj[element.name]}</span>}
                      disabled={params.isRCSUnchecked}
                      onChange={(e) => {
                        const { checked } = e.target;
                        if (checked) {
                          onParamsChanged({
                            usedBuildingBlocks: buildingBlockElements
                              ?.filter(
                                (block) =>
                                  element.name === block.name ||
                                  (block.name !== maxShippingTimeToggleHelperObj[element.name] &&
                                    params?.usedBuildingBlocks?.indexOf(block.name) >= 0)
                              )
                              ?.map((block) => block.name),
                          });
                        }
                      }}
                    />
                  ))}
                {!find(buildingBlockElements, (item) => item.name === RCSANY) && (
                  <RadioButtonGroup.Option
                    key={RCSANY}
                    className="checkbox_inline_right e2e-checkbox-max-shipping-time"
                    checked={false}
                    label={<span>{maxShippingTimeDisplayHelperObj[RCSANY]}</span>}
                    disabled
                  />
                )}
              </PopupRow>
              <PopupRow className="rs-subrow mgt rcs-subrow">
                <TextInput
                  value={params.maxPricePerGram || ''}
                  label="Max. Price per gram:"
                  isLabelInline
                  size="small"
                  data-e2e="iktos-max-price-per-gram-input"
                  placeholder="any"
                  className="iktos-max-price-per-gram-input"
                  onChange={(e) => onParamsChanged({ maxPricePerGram: e.target.value })}
                  disabled={params.isRCSUnchecked}
                />
                ($/g)
                <HeaderInfoIcon
                  tooltipId="info-tooltip-predicted-max-price"
                  e2eName="info-tooltip-predicted-max-price"
                  ariaLabel={PREDICTED_PARAMS_MAX_PRICE_LABEL}
                />
                <HeaderInfoTooltip id="info-tooltip-predicted-max-price">
                  <div className="rs-tooltip-big__content">
                    The “Max. Price per gram” parameter will limit the building block pool to commercial substances that do not cost more
                    than the given maximum price per gram. “Any” indicates that the filter is not active. Please click the edit box to enter
                    any integer numbers &gt; 0. Decimals are not supported.
                  </div>
                </HeaderInfoTooltip>
              </PopupRow>
              <div
                className={classnames('max-price-gm-validation-messege', {
                  'not-visible': checkValidMaxPriceOfGrams(params.maxPricePerGram),
                })}>
                Please enter integers &gt;0 only
              </div>
            </>
          )}
        </>
      )}
      {showSMSCheckbox()
        ? buildingBlockElements
            .filter((block) => [RCS10D, RCSANY].indexOf(block.name) < 0)
            .map((element) => {
              const checkboxStateChecked = params.usedBuildingBlocks && params.usedBuildingBlocks.indexOf(element.name) >= 0;
              return (
                <PopupRow className="rs-subrow mgt" key={element.name}>
                  <Checkbox
                    className="checkbox_no-padding e2e-checkbox-building-block"
                    checked={checkboxStateChecked || false}
                    data-e2e={`iktos-building-block-checkbox-${element.name}`}
                    onChange={(event) => {
                      const { checked: isChecked } = event.target;
                      onParamsChanged({
                        usedBuildingBlocks: buildingBlockElements
                          ?.filter(
                            (block) =>
                              (element.name === block.name && isChecked) ||
                              (element.name !== block.name && params.usedBuildingBlocks.indexOf(block.name) >= 0)
                          )
                          .map((block) => block.name),
                      });
                    }}
                    label={`${element.label} (${element.name})`}
                  />
                </PopupRow>
              );
            })
        : buildingBlockElements
            ?.filter((block) => [RCS10D, RCSANY].indexOf(block.name) < 0)
            .map((element) => (
              <PopupRow className="rs-subrow mgt" key={element.name}>
                <span>{`${element.label} (${element.name})`}</span>
              </PopupRow>
            ))}

      {isRCSAvailable && isRPSAvailable && (
        <>
          <hr className="grey-line" />
          <PopupRow className="rs-column-subheader">
            Published Routes Integration
            <HeaderInfoIcon
              tooltipId="info-tooltip-predicted-published-routes-integration"
              e2eName="info-tooltip-predicted-published-routes-integration"
              ariaLabel="Published Routes Integration Information"
              informationIconTitle="Published Routes Integration Information"
            />
            <HeaderInfoTooltip id="info-tooltip-predicted-published-routes-integration">
              <div className="rs-tooltip-big__content">
                <div className="u-els-font-size-caption u-els-margin-bottom">Published Routes Integration</div>
                <div>
                  Merges literature routes with predicted routes, adding literature-based steps for non‑commercial intermediates and linking
                  them to the predicted pathway. It also creates combined routes by integrating established literature reactions with
                  computer‑generated steps, starting from your configured starting-material settings. Published routes are limited to five
                  steps and use the cheapest available building blocks.
                </div>
              </div>
            </HeaderInfoTooltip>
          </PopupRow>
          <PopupRow className="mgt">
            <SlideSwitch
              disabled={!isRCSChecked()}
              data-e2e="iktos-rps-switch"
              checked={params.isRPSChecked || false}
              onChange={onRPSSwitchChange}
              label={
                <>
                  <RPSSwitchIcon />
                  <span className="u-els-display-inline-block">Combine Predicted and Published routes</span>
                </>
              }
              switchRight
            />
          </PopupRow>
        </>
      )}
      <hr className="grey-line" />
      <PopupRow className="rs-column-subheader">
        Processing time
        <HeaderInfoIcon
          tooltipId="info-tooltip-predicted-processing"
          e2eName="info-tooltip-predicted-processing"
          ariaLabel={PREDICTED_PARAMS_PROCESSING_TIME_LABEL}
        />
        <HeaderInfoTooltip id="info-tooltip-predicted-processing">
          <div className="rs-tooltip-big__content">
            As the retrosynthesis process can work virtually indefinitely, a processing time limit has to be defined. By the Standard
            option, retrosynthesis computes predictive results for approximately 10 minutes. In case no results have been found with the
            standard option you can set the processing time to Extended, which will run for approximately 30 minutes.
          </div>
        </HeaderInfoTooltip>
      </PopupRow>
      <PopupRow className="mgt rs-subrow">
        {PREDICTED_IKTOS_PROCESSING_TIME_INTERVALS.map((interval) => (
          <RadioButtonGroup.Option
            key={interval.value}
            className={`checkbox_inline_right e2e-checkbox-processingTime-${interval.value}`}
            checked={interval.value === params.processingTime}
            label={<span>{interval.label}</span>}
            onChange={(e) => {
              const { checked } = e.target;
              if (checked) {
                onParamsChanged({ processingTime: interval.value });
              }
            }}
          />
        ))}
      </PopupRow>
    </>
  );
}

PredictedParametersIktosGeneral.defaultProps = {
  onParamsChanged: () => null,
  buildingBlockElements: [],
  handleValidationError: () => null,
};

PredictedParametersIktosGeneral.propTypes = {
  onParamsChanged: PropTypes.func,
  params: PropTypes.object,
  buildingBlockElements: PropTypes.array,
  handleValidationError: PropTypes.func,
  defaultMaxNoOfSteps: PropTypes.number,
};

export default PredictedParametersIktosGeneral;
